import { memo, useEffect, useState } from 'react';
import { Grid, Button, Dialog, Skeleton, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';

import useActionCluster from '@shared/hooks/useActionCluster';
import { getPublicParameters } from '@shared/services/services';

import Cluster from './Cluster';
import ClusterPersonalized from './ClusterPersonalized';

import ModalMessages from './ModalMessages/ModalMessages';
import BaggageModal from './BaggageModal';

import { sxStyled } from './styles';
import { getElem, getSafeClusterId, getClusterId } from '../helpers';

const Clusters = memo(
  ({
    flightsDta,
    flightsCustomDta,
    viewOption,
    viewItems,
    handleSeeMore,
    setIsScroll,
    flightsInUSD,
    handleOpenDrawer,
    screenSize,
    CanSaleInUsd,
    isCustomSearch,
    businessTypes,
    clusterRefs,
    isDrawer,
  }) => {
    const theme = useTheme();
    const sxStyle = sxStyled(theme);

    const [canBuy, setCanBuy] = useState(false);
    const [canBook, setCanBook] = useState(false);
    const [passengers, setPassengers] = useState({
      adults: 0,
      childs: 0,
      childsBaby: 0,
    });

    const flightsSearch = useSelector(store => store.flightsSearch);
    const infoFlightsSearch = flightsSearch[flightsSearch?.currentTripType];

    useEffect(() => {
      fetchPublicParameters();

      if (!infoFlightsSearch) return;

      const { adults_qty = 0, childs = [] } = infoFlightsSearch;
      let adults = adults_qty;

      const { childsCount, childsBaby, adultsFromChilds } = childs.reduce(
        (acc, { age }) => {
          if (age <= 1) acc.childsBaby++;
          else if (age >= 2 && age <= 11) acc.childsCount++;
          else if (age >= 12) acc.adultsFromChilds++;
          return acc;
        },
        { childsCount: 0, childsBaby: 0, adultsFromChilds: 0 }
      );

      adults += adultsFromChilds;

      setPassengers({ adults, childs: childsCount, childsBaby });
    }, [infoFlightsSearch]);

    const fetchPublicParameters = async () => {
      let isInternational = false;

      if (flightsDta[0].cluster.composition_price.tax_detail?.QO) {
        isInternational = true;
      }

      const params = { parameter_types: 5 };
      const response = await getPublicParameters(params);

      const salesNames = {
        true: 'salesFlightsInternational',
        false: 'salesFlightsNational',
      };

      const bookNames = {
        true: 'bookFlightsInternational',
        false: 'bookFlightsNational',
      };

      const sales = salesNames[isInternational];
      const booking = bookNames[isInternational];

      setCanBuy(response.find(x => x.name === sales)?.value);
      setCanBook(response.find(x => x.name === booking)?.value);
    };

    const ClusterRender = isCustomSearch ? ClusterPersonalized : Cluster;

    const data = isCustomSearch ? flightsCustomDta : flightsDta;

    const {
      openModalBaggage,
      setOpenModalBaggage,
      differenceBaggage,
      setDifferenceBaggage,
      selectedOption,
      openModalMessage,
      setOpenModalMessage,
      notDisponible,
      setNotDisponible,
      difference,
      validateBaggage,
      selectedFlightBaggage,
    } = useActionCluster(setIsScroll, flightsInUSD, infoFlightsSearch);

    return (
      <>
        {data.length > 0 ? (
          data.map((element, index) => {
            const elem = getElem(element, isCustomSearch);

            const clusterId = getSafeClusterId(
              getClusterId(elem),
              isCustomSearch
            );

            const setClusterRef = el => {
              if (!clusterRefs?.current) return;

              clusterRefs.current[clusterId] = el;
            };

            return (
              <div key={`cluster-${index}-${clusterId}`} ref={setClusterRef}>
                <ClusterRender
                  element={element}
                  index={index}
                  infoFlightsSearch={infoFlightsSearch}
                  setIsScroll={setIsScroll}
                  flightsInUSD={flightsInUSD}
                  canBuy={canBuy}
                  canBook={canBook}
                  handleOpenDrawer={handleOpenDrawer}
                  isCustomSearch={isCustomSearch}
                  screenSize={screenSize}
                  validateBaggage={validateBaggage}
                  CanSaleInUsd={CanSaleInUsd}
                  businessTypes={businessTypes}
                  passengers={passengers}
                  isDrawer={isDrawer}
                />
              </div>
            );
          })
        ) : (
          <Grid
            container
            sx={sxStyle.cardGrid}
            style={{ padding: '0px 10px', flexDirection: 'row' }}
          >
            <Grid item xs={12} md={4} style={{ padding: '20px 10px' }}>
              <Skeleton variant="text" height={50} />
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={65} />
              <Skeleton variant="text" height={65} />
              <Skeleton variant="text" height={65} />
              <Skeleton variant="text" height={65} />
            </Grid>
            <Grid item xs={12} md={4} style={{ padding: '20px 10px' }}>
              <Skeleton variant="text" height={50} />
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={65} />
              <Skeleton variant="text" height={65} />
              <Skeleton variant="text" height={65} />
            </Grid>
            <Grid item xs={12} md={4} style={{ padding: '20px 10px' }}>
              <Skeleton variant="rect" height={325} />
            </Grid>
          </Grid>
        )}

        {!isCustomSearch && (
          <Grid item xs={12} md={8} sx={{ textAlign: 'center' }}>
            <Button
              sx={sxStyle.pagesButton}
              onClick={() => handleSeeMore()}
              disabled={viewItems >= viewOption}
            >
              Ver más opciones
            </Button>
          </Grid>
        )}

        {openModalBaggage && (
          <Dialog
            open={openModalBaggage}
            onClose={() => {
              setOpenModalBaggage(false);
            }}
            PaperProps={{
              sx: {
                p: 2,
                width: 'fit-content',
                maxWidth: 'none',
              },
            }}
            disableEnforceFocus
          >
            <BaggageModal
              closeModal={() => {
                setOpenModalBaggage(false);
                setDifferenceBaggage([]);
              }}
              differenceBaggage={differenceBaggage}
              selectedOption={selectedOption}
              selectedFlightBaggage={selectedFlightBaggage}
            />
          </Dialog>
        )}

        {openModalMessage && (
          <Dialog
            open={openModalMessage}
            onClose={() => setOpenModalMessage(false)}
            fullWidth
            maxWidth="sm"
            disableEnforceFocus
          >
            <ModalMessages
              difference={difference}
              closeModal={() => {
                setOpenModalMessage(false);
                setNotDisponible(false);
              }}
              selectedOption={selectedOption}
              notDisponible={notDisponible}
              flightsInUSD={flightsInUSD}
            />
          </Dialog>
        )}
      </>
    );
  }
);

export default Clusters;
